import { useState, useEffect } from 'react';
import { Button, Spinner, Badge, Form, InputGroup, Alert, Table } from 'react-bootstrap';
import {
  getKohoAllCustomers,
  getKohoOffersByCustomer,
  getKohoProjects,
  getKohoContracts,
  getKohoInvoices,
  getKohoEmployees,
  getKohoWorkSessions,
  getKohoExpenses,
  getKohoProjectTasksByCustomer,
  getKohoProjectPlannedLoads,
  getKohoInvoiceTermsByContract,
  getKohoInvoicePendingSessions,
} from '../api/api';

function matchesQuery(c, q) {
  const lq = q.toLowerCase();
  return (
    c.name?.toLowerCase().includes(lq) ||
    c.delivery_address?.toLowerCase().includes(lq) ||
    c.address?.toLowerCase().includes(lq) ||
    c.delivery_city?.toLowerCase().includes(lq) ||
    c.city?.toLowerCase().includes(lq)
  );
}

function renderWithLinks(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s]+)/);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{part}</a>
      : part
  );
}

function moneyFmt(value, suffix = ' €') {
  return <span style={{ fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value.toLocaleString('fi-FI')}{suffix}</span>;
}

function offerOutcomeBadge(outcome) {
  if (!outcome) return <Badge bg="secondary" className="fw-normal">Avoin</Badge>;
  const map = { accepted: ['success', 'Hyväksytty'], won: ['success', 'Voitettu'], lost: ['danger', 'Hävitty'], cancelled: ['secondary', 'Peruutettu'] };
  const [bg, label] = map[outcome] ?? ['secondary', outcome];
  return <Badge bg={bg} className="fw-normal">{label}</Badge>;
}

export default function KohoLinkki({ card }) {
  const defaultSearch = card?.kohteen_nimi || card?.rakennukset_fulls?.[0]?.osoite || '';
  const [searchTerm, setSearchTerm] = useState(defaultSearch);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const [projects, setProjects] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [employeesById, setEmployeesById] = useState(new Map());
  const [workSessions, setWorkSessions] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [plannedLoadsMap, setPlannedLoadsMap] = useState(new Map());
  const [pendingSessionsMap, setPendingSessionsMap] = useState(new Map());
  const [invoiceTermsMap, setInvoiceTermsMap] = useState(new Map());
  const [detailLoading, setDetailLoading] = useState(false);
  const [offerFilter, setOfferFilter] = useState('hyvaksytyt');
  const [kohoTab, setKohoTab] = useState('tarjoukset');

  useEffect(() => {
    if (defaultSearch) search(true);
  }, []);

  const search = async (autoSelect = false) => {
    setLoading(true);
    setError(null);
    setResults(null);
    setSelected(null);
    setProjects([]); setContracts([]); setInvoices([]); setOffers([]); setEmployeesById(new Map());
    setWorkSessions([]); setExpenses([]); setProjectTasks([]);
    setPlannedLoadsMap(new Map()); setPendingSessionsMap(new Map()); setInvoiceTermsMap(new Map());
    try {
      const res = await getKohoAllCustomers();
      const customers = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
      const q = searchTerm.trim();
      const matched = q ? customers.filter(c => matchesQuery(c, q)) : customers;
      const top = matched.slice(0, 10);
      if (autoSelect && top.length > 0) {
        await selectCustomer(top[0]);
      } else {
        setResults(top);
      }
    } catch (e) {
      setError('KOHO-haku epäonnistui: ' + (e.message ?? e));
    } finally {
      setLoading(false);
    }
  };

  const selectCustomer = async (customer) => {
    setSelected(customer);
    setResults(null);
    setDetailLoading(true);
    try {
      const extract = (r, fallback = []) => {
        if (r.status !== 'fulfilled') return fallback;
        const d = r.value.data;
        return Array.isArray(d) ? d : (d?.results ?? d?.offers ?? d?.projects ?? d?.contracts ?? d?.invoices ?? fallback);
      };

      // Phase 1: all customer-level fetches in parallel
      const [projRes, contRes, invRes, offRes, empRes, wsRes, expRes, tasksRes] = await Promise.allSettled([
        getKohoProjects({ customer_id: customer.id }),
        getKohoContracts({ customer_id: customer.id }),
        getKohoInvoices({ customer_id: customer.id }),
        getKohoOffersByCustomer(customer.id),
        getKohoEmployees(),
        getKohoWorkSessions({ 'customer_id[]': customer.id }),
        getKohoExpenses({ 'customer_id[]': customer.id }),
        getKohoProjectTasksByCustomer(customer.id),
      ]);

      const fetchedProjects = extract(projRes);
      const fetchedContracts = extract(contRes);
      setProjects(fetchedProjects);
      setContracts(fetchedContracts);
      setInvoices(extract(invRes));
      setOffers(extract(offRes));
      setEmployeesById(new Map(extract(empRes).map(e => [e.id, e.name])));
      setWorkSessions(extract(wsRes));
      setExpenses(extract(expRes));
      setProjectTasks(extract(tasksRes));

      // Phase 2: per-project and per-contract fetches in parallel
      const projectIds = fetchedProjects.map(p => p.id);
      const contractIds = fetchedContracts.map(c => c.id);

      const [loadsResults, pendingResults, termsResults] = await Promise.all([
        Promise.allSettled(projectIds.map(id =>
          getKohoProjectPlannedLoads(id).then(r => ({ id, data: Array.isArray(r.data) ? r.data : [] }))
        )),
        Promise.allSettled(projectIds.map(id =>
          getKohoInvoicePendingSessions(id).then(r => ({ id, data: Array.isArray(r.data) ? r.data : [] }))
        )),
        Promise.allSettled(contractIds.map(id =>
          getKohoInvoiceTermsByContract(id).then(r => ({ id, data: Array.isArray(r.data) ? r.data : [] }))
        )),
      ]);

      setPlannedLoadsMap(new Map(
        loadsResults.filter(r => r.status === 'fulfilled').map(r => [r.value.id, r.value.data])
      ));
      setPendingSessionsMap(new Map(
        pendingResults.filter(r => r.status === 'fulfilled').map(r => [r.value.id, r.value.data])
      ));
      setInvoiceTermsMap(new Map(
        termsResults.filter(r => r.status === 'fulfilled').map(r => [r.value.id, r.value.data])
      ));
    } finally {
      setDetailLoading(false);
    }
  };

  const clearSelected = () => {
    setSelected(null);
    setProjects([]); setContracts([]); setInvoices([]); setOffers([]); setEmployeesById(new Map());
    setWorkSessions([]); setExpenses([]); setProjectTasks([]);
    setPlannedLoadsMap(new Map()); setPendingSessionsMap(new Map()); setInvoiceTermsMap(new Map());
  };

  return (
    <div className="p-3" style={{ overflowX: 'hidden' }}>
      <h6 className="fw-bold mb-3" style={{ color: '#04aa00' }}>KOHO-asiakas</h6>

      {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

      <InputGroup className="mb-2">
        <Form.Control
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Kohteen nimi tai osoite"
          size="sm"
        />
        <Button variant="success" size="sm" onClick={() => search()} disabled={loading}>
          {loading ? <Spinner size="sm" /> : 'Hae KOHOsta'}
        </Button>
      </InputGroup>

      {results !== null && results.length === 0 && (
        <p className="text-muted small">Ei hakutuloksia.</p>
      )}

      {results?.length > 0 && (
        <div className="border rounded mb-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
          {results.map(c => (
            <div key={c.id} className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <div>
                <div className="fw-semibold small">{c.name}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  {c.organization_id && <span className="me-2">{c.organization_id}</span>}
                  {c.delivery_address || c.address}
                  {(c.delivery_city || c.city) && `, ${c.delivery_city || c.city}`}
                </div>
              </div>
              <Button size="sm" variant="outline-success" onClick={() => selectCustomer(c)}>
                Valitse
              </Button>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <>
          {/* Header row */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div className="fw-bold" style={{ fontSize: '1.05rem' }}>{selected.name}</div>
              {selected.organization_id && (
                <Badge bg="secondary" className="me-1 mb-1">{selected.organization_id}</Badge>
              )}
            </div>
            <Button size="sm" variant="outline-secondary" onClick={clearSelected}>✕</Button>
          </div>

          {/* Tab strip */}
          <div className="d-flex gap-1 mb-3 border-bottom pb-2">
            {[
              { key: 'tarjoukset',       label: 'Tarjoukset',      icon: 'bi-briefcase' },
              { key: 'asiakkaan_tiedot', label: 'Asiakkaan tiedot', icon: 'bi-person-vcard' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setKohoTab(t.key)}
                className="btn btn-sm"
                style={{
                  borderRadius: 20,
                  fontSize: '0.8rem',
                  padding: '2px 12px',
                  background: kohoTab === t.key ? '#04aa00' : 'transparent',
                  color: kohoTab === t.key ? '#fff' : '#6c757d',
                  border: kohoTab === t.key ? '1px solid #04aa00' : '1px solid #dee2e6',
                  fontWeight: kohoTab === t.key ? 600 : 400,
                }}
              >
                <i className={`bi ${t.icon} me-1`} />{t.label}
              </button>
            ))}
          </div>

          {kohoTab === 'asiakkaan_tiedot' && <>
          {/* Billing info */}
          <div className="border rounded p-3 mb-3 bg-light">
            <div className="mb-2 fw-semibold" style={{ fontSize: '0.9rem' }}>
              <i className="bi bi-receipt me-1" />Laskutustiedot
            </div>
            <div className="row row-cols-2 row-cols-md-3 g-2" style={{ fontSize: '0.82rem' }}>
              {[
                ['Laskutusnimi',      selected.invoice_name],
                ['Y-tunnus',          selected.organization_id],
                ['ALV-tunnus',        selected.vat_id],
                ['Puhelin',           selected.phone],
                ['Sähköposti',        selected.billing_email || selected.email],
                ['Laskutusosoite',    [selected.address, selected.zip_code, selected.city].filter(Boolean).join(', ')],
                ['Toimitusosoite',    [selected.delivery_address, selected.delivery_zip_code, selected.delivery_city].filter(Boolean).join(', ')],
                ['OVT-tunnus',        selected.ovt_identifier],
                ['Välittäjätunnus',   selected.electronic_invoicing_intermediary],
                ['Laskutustapa',      selected.default_invoice_delivery_method],
                ['Verkkolaskuosoite', selected.web_invoice_address],
                ['Maksuehto',         selected.default_payment_term != null ? `${selected.default_payment_term} pv` : null],
                ['Valuutta',          selected.currency],
                ['Nollaverollinen',   selected.zero_vat ? 'Kyllä' : null],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  <div className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</div>
                  <div className="fw-semibold">{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Persons */}
          {selected.persons?.filter(p => p.first_name || p.last_name).length > 0 && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="mb-2 fw-semibold" style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-person me-1" />Yhteyshenkilöt
              </div>
              <div className="d-flex flex-wrap gap-2">
                {selected.persons.filter(p => p.first_name || p.last_name).map(p => (
                  <div key={p.id} className="border rounded px-2 py-1 bg-white" style={{ fontSize: '0.8rem' }}>
                    <div className="fw-semibold">{[p.first_name, p.last_name].filter(Boolean).join(' ')}</div>
                    {p.position && <div className="text-muted">{p.position}</div>}
                    {p.email && <div className="text-muted">{p.email}</div>}
                    {p.phone && <div className="text-muted">{p.phone}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Employees */}
          {selected.employees?.filter(e => e.first_name || e.last_name).length > 0 && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="mb-2 fw-semibold" style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-people me-1" />Vastuuhenkilöt
              </div>
              <div className="d-flex flex-wrap gap-2">
                {selected.employees.filter(e => e.first_name || e.last_name).map(e => (
                  <div key={e.id} className="border rounded px-2 py-1 bg-white" style={{ fontSize: '0.8rem' }}>
                    <div className="fw-semibold">{[e.first_name, e.last_name].filter(Boolean).join(' ')}</div>
                    {e.position && <div className="text-muted">{e.position}</div>}
                    {e.email && <div className="text-muted">{e.email}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expenses */}
          {expenses.length > 0 && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="mb-2 fw-semibold" style={{ fontSize: '0.9rem' }}>
                <i className="bi bi-receipt-cutoff me-1" />Kulut ({expenses.length})
              </div>
              <div className="d-flex flex-column gap-1">
                {expenses.map(e => (
                  <div key={e.id} className="border rounded px-2 py-1 bg-white" style={{ fontSize: '0.82rem' }}>
                    <div className="d-flex justify-content-between align-items-baseline gap-2">
                      <span className="fw-semibold">{e.name || '—'}</span>
                      <span className="text-muted text-nowrap">{e.start_date}{e.end_date && e.end_date !== e.start_date ? ` – ${e.end_date}` : ''}</span>
                    </div>
                    {e.description && <div className="text-muted" style={{ fontSize: '0.75rem' }}>{e.description}</div>}
                    <div className="d-flex flex-wrap gap-3 mt-1" style={{ fontSize: '0.75rem' }}>
                      {e.journeys?.length > 0 && <span className="text-muted">Matkat: {e.journeys.length} kpl</span>}
                      {e.allowances?.length > 0 && <span className="text-muted">Päivärahat: {e.allowances.length} kpl</span>}
                      {e.costs?.length > 0 && <span className="text-muted">Kulut: {e.costs.length} kpl</span>}
                      {e.confirmed && <Badge bg="success" style={{ fontSize: '0.63rem' }}>Vahvistettu</Badge>}
                      {e.approved_by_manager && <Badge bg="primary" style={{ fontSize: '0.63rem' }}>Hyväksytty</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          </>}

          {kohoTab === 'tarjoukset' && (detailLoading ? (
            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
          ) : (
            <>
              {/* Single view — no tab strip needed */}

              {/* Kaupallinen: true nesting Tarjous → Sopimus (+ laskut) → Projekti (+ laskut) */}
              <div>
                {offers.length === 0 && contracts.length === 0 && projects.length === 0 && (
                  <p className="text-muted small">Ei kaupallisia tietoja.</p>
                )}

                {/* Nested tree: Tarjous → Sopimus → Projekti */}
                {(() => {
                  const renderInvoiceFooter = (invList) => {
                    if (!invList.length) return null;
                    return (
                      <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                        <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6c757d' }}>
                          <i className="bi bi-receipt me-1" />Laskut ({invList.length})
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <Table size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                            <thead className="table-light">
                              <tr>
                                <th>Nimi</th>
                                <th>Laskupäivä</th>
                                <th>Eräpäivä</th>
                                <th className="text-end">Netto (€)</th>
                                <th>Tila</th>
                              </tr>
                            </thead>
                            <tbody>
                              {invList.map(inv => (
                                <tr key={inv.id}>
                                  <td>{inv.name ?? `Lasku #${inv.id}`}</td>
                                  <td className="text-muted">{inv.billing_date ?? '—'}</td>
                                  <td className="text-muted">{inv.due_date ?? '—'}</td>
                                  <td className="text-end">{inv.net_amount != null ? moneyFmt(inv.net_amount) : '—'}</td>
                                  <td>
                                    {inv.paid
                                      ? <Badge bg="success" style={{ fontSize: '0.63rem' }}>Maksettu</Badge>
                                      : inv.sent
                                        ? <Badge bg="warning" text="dark" style={{ fontSize: '0.63rem' }}>Lähetetty</Badge>
                                        : <Badge bg="secondary" style={{ fontSize: '0.63rem' }}>Luonnos</Badge>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    );
                  };

                  const renderProject = (p) => {
                    const thisTasks = projectTasks.filter(t => t.project_id === p.id);
                    const thisSessions = workSessions.filter(s => s.project_id === p.id);
                    const totalHours = thisSessions.reduce((s, ws) => s + (ws.hours ?? 0), 0);
                    const totalInvoiceHours = thisSessions.reduce((s, ws) => s + (ws.invoice_hours ?? 0), 0);
                    const totalNet = thisSessions.reduce((s, ws) => s + (ws.invoice_net_amount ?? 0), 0);
                    const thisLoads = plannedLoadsMap.get(p.id) ?? [];
                    const thisPending = pendingSessionsMap.get(p.id) ?? [];
                    const linkedContract = p.contract_id ? contracts.find(c => c.id === p.contract_id) : null;
                    const contractTotal = linkedContract?.one_time_net_total ?? null;
                    const perPerson = contractTotal != null && p.participations?.length > 0
                      ? contractTotal / p.participations.length : null;
                    return (
                      <div key={p.id} className="rounded p-2 mt-2"
                        style={{ fontSize: '0.81rem', background: '#fff8f0', border: '1px solid #ffe0b2', borderLeft: '3px solid #fd7e14' }}>
                        <div className="d-flex align-items-center gap-1 mb-1" style={{ color: '#e65100', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          <i className="bi bi-kanban" />
                          Projekti{p.external_project_key ? ` #${p.external_project_key}` : ''}
                        </div>
                        <div className="d-flex flex-wrap align-items-start gap-2 mb-1">
                          <div className="fw-semibold" style={{ fontSize: '0.86rem' }}>
                            {p.name}
                            <span className="ms-2">
                              {p.active === false
                                ? <Badge bg="secondary" style={{ fontSize: '0.63rem' }}>Ei aktiivinen</Badge>
                                : <Badge bg="success" style={{ fontSize: '0.63rem' }}>Aktiivinen</Badge>}
                            </span>
                          </div>
                          {p.phase && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Vaihe</div><div>{p.phase}</div></div>}
                          {p.start_date && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Alkaa</div><div>{p.start_date}</div></div>}
                          {p.end_date && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Päättyy</div><div>{p.end_date}</div></div>}
                          {p.created_at && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Luotu</div><div>{p.created_at.slice(0, 10)}</div></div>}
                        </div>
                        {(p.price_per_hour != null || p.budget_total != null || p.net_total_budget != null || p.invoicable_budget != null || p.hours_budget != null || p.contract_hours != null || (p.trip_kms != null && p.trip_kms !== 0)) && (
                          <div className="d-flex flex-wrap gap-3 mb-1 pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            {p.price_per_hour != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Tuntihinta</div><div>{moneyFmt(p.price_per_hour, ' €/h')}</div></div>}
                            {p.budget_total != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Budjetti</div><div>{moneyFmt(p.budget_total)}</div></div>}
                            {p.net_total_budget != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Nettobudjetti</div><div>{moneyFmt(p.net_total_budget)}</div></div>}
                            {p.invoicable_budget != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Laskutettava</div><div>{moneyFmt(p.invoicable_budget)}</div></div>}
                            {p.hours_budget != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Tuntikiintiö</div><div>{p.hours_budget} h</div></div>}
                            {p.contract_hours != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Sopimustunnit</div><div>{p.contract_hours} h</div></div>}
                            {p.trip_kms != null && p.trip_kms !== 0 && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Matkat</div><div>{p.trip_kms} km</div></div>}
                          </div>
                        )}
                        {(p.our_reference || p.your_reference) && (
                          <div className="d-flex gap-3 mb-1 pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            {p.our_reference && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Viitteemme</div><div>{p.our_reference}</div></div>}
                            {p.your_reference && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Viitteenne</div><div>{p.your_reference}</div></div>}
                          </div>
                        )}
                        {p.participations?.length > 0 && (
                          <div className="mb-1 pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>
                              Osallistujat
                              {perPerson != null && (
                                <span className="ms-2 fw-normal" style={{ textTransform: 'none', color: '#6c757d' }}>
                                  ({contractTotal.toLocaleString('fi-FI')} € ÷ {p.participations.length} = {perPerson.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} € / hlö)
                                </span>
                              )}
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                            <Table size="sm" className="align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th style={{ fontSize: '0.7rem' }}>Henkilö</th>
                                  <th style={{ fontSize: '0.7rem' }}>Kuormitus</th>
                                  {perPerson != null && <th style={{ fontSize: '0.7rem' }}>Osuus</th>}
                                  <th style={{ fontSize: '0.7rem' }}>Alkaa</th>
                                  <th style={{ fontSize: '0.7rem' }}>Päättyy</th>
                                  <th style={{ fontSize: '0.7rem' }}>Rooli</th>
                                </tr>
                              </thead>
                              <tbody>
                                {p.participations.map(part => (
                                  <tr key={part.id}>
                                    <td style={{ fontSize: '0.73rem' }}><strong>{employeesById.get(part.employee_id) ?? `#${part.employee_id}`}</strong></td>
                                    <td style={{ fontSize: '0.73rem' }}>{part.load != null ? `${part.load} h` : '—'}</td>
                                    {perPerson != null && <td style={{ fontSize: '0.73rem' }} className="text-success fw-semibold">{perPerson.toLocaleString('fi-FI', { maximumFractionDigits: 2 })} €</td>}
                                    <td style={{ fontSize: '0.73rem' }} className="text-muted">{part.start ?? '—'}</td>
                                    <td style={{ fontSize: '0.73rem' }} className="text-muted">{part.end ?? '—'}</td>
                                    <td style={{ fontSize: '0.73rem' }}>
                                      {part.responsible && <Badge bg="success" className="fw-normal me-1" style={{ fontSize: '0.63rem' }}>Vastuuhenkilö</Badge>}
                                      {part.sales_role && <Badge bg="info" className="fw-normal" style={{ fontSize: '0.63rem' }}>Myynti</Badge>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            </div>
                          </div>
                        )}
                        {p.description && (
                          <div className="pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Kuvaus</div>
                            <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{renderWithLinks(p.description)}</div>
                          </div>
                        )}

                        {/* Project tasks */}
                        {thisTasks.length > 0 && (
                          <div className="pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Tehtävät</div>
                            <div className="d-flex flex-column gap-1">
                              {thisTasks.filter(t => !t.hidden).map(t => (
                                <div key={t.id} className="d-flex justify-content-between align-items-baseline gap-2" style={{ fontSize: '0.78rem' }}>
                                  <span>{t.name}{t.code ? ` (${t.code})` : ''}</span>
                                  <span className="text-muted text-nowrap">{t.hour_budget != null ? `${t.hour_budget} h` : ''}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Work sessions summary */}
                        {thisSessions.length > 0 && (
                          <div className="pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Työtunnit</div>
                            <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.78rem' }}>
                              <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Kirjattu</div><div>{totalHours.toLocaleString('fi-FI', { maximumFractionDigits: 1 })} h</div></div>
                              {totalInvoiceHours !== totalHours && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Laskutettava</div><div>{totalInvoiceHours.toLocaleString('fi-FI', { maximumFractionDigits: 1 })} h</div></div>}
                              {totalNet !== 0 && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Laskutettu</div><div>{moneyFmt(totalNet)}</div></div>}
                              <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Kirjauksia</div><div>{thisSessions.length} kpl</div></div>
                            </div>
                          </div>
                        )}

                        {/* Planned loads */}
                        {thisLoads.length > 0 && (
                          <div className="pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6c757d' }}>Suunnitellut kuormitukset</div>
                            <div className="d-flex flex-column gap-1">
                              {thisLoads.map((l, i) => (
                                <div key={i} className="d-flex flex-wrap gap-2" style={{ fontSize: '0.78rem' }}>
                                  {l.employee_id && <span className="fw-semibold">{employeesById.get(l.employee_id) ?? `#${l.employee_id}`}</span>}
                                  {l.hours != null && <span>{l.hours} h</span>}
                                  {l.start_date && <span className="text-muted">{l.start_date}{l.end_date ? ` – ${l.end_date}` : ''}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Invoice pending sessions */}
                        {thisPending.length > 0 && (
                          <div className="pt-1" style={{ borderTop: '1px solid #ffe0b2' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#e65100' }}>
                              Laskuttamatta ({thisPending.length} kirjausta)
                            </div>
                            <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.78rem' }}>
                              <div>
                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>Tunteja</div>
                                <div>{thisPending.reduce((s, ws) => s + (ws.invoice_hours ?? ws.hours ?? 0), 0).toLocaleString('fi-FI', { maximumFractionDigits: 1 })} h</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  };

                  const renderContract = (c) => {
                    const thisTerms = invoiceTermsMap.get(c.id) ?? [];
                    const childProjects = projects.filter(p => p.contract_id === c.id);
                    return (
                      <div key={c.id} className="rounded p-2 mt-2"
                        style={{ fontSize: '0.83rem', background: '#f0f4ff', border: '1px solid #c5cff7', borderLeft: '3px solid #4c6ef5' }}>
                        <div className="d-flex align-items-center gap-1 mb-1" style={{ color: '#364fc7', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          <i className="bi bi-file-earmark-text" />
                          Sopimus
                          {c.archived && <Badge bg="secondary" className="ms-1 fw-normal" style={{ fontSize: '0.63rem' }}>Arkistoitu</Badge>}
                        </div>
                        <div className="d-flex flex-wrap align-items-start gap-2 mb-1">
                          <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>{c.name || `#${c.id}`}</div>
                          {c.start_date && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Alkaa</div><div>{c.start_date}</div></div>}
                          {c.end_date && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Päättyy</div><div>{c.end_date}</div></div>}
                          {c.billing_interval && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Laskutusväli</div><div>{c.billing_interval}</div></div>}
                          {c.one_time_net_total != null && <div><div className="text-muted" style={{ fontSize: '0.7rem' }}>Summa</div><div>{moneyFmt(c.one_time_net_total)}</div></div>}
                        </div>
                        {c.products?.length > 0 && (
                          <div className="mb-1">
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#364fc7' }}>Tuotteet</div>
                            <div className="d-flex flex-column gap-1">
                              {c.products.map((prod, i) => (
                                <div key={prod.id ?? i} className="rounded px-2 py-1 bg-white" style={{ fontSize: '0.78rem', border: '1px solid #c5cff7' }}>
                                  <div className="d-flex justify-content-between align-items-baseline gap-2">
                                    <span className="fw-semibold">{prod.name || '—'}</span>
                                    {prod.price != null && <span className="text-nowrap">{moneyFmt(prod.price, prod.monthly ? ' €/kk' : ' €')}</span>}
                                  </div>
                                  {prod.description && <div className="text-muted">{prod.description}</div>}
                                  <div className="d-flex gap-3 text-muted" style={{ fontSize: '0.73rem' }}>
                                    {prod.count != null && <span>{prod.count} kpl</span>}
                                    {prod.vat != null && <span>alv {prod.vat}%</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Invoice terms */}
                        {thisTerms.length > 0 && (
                          <div className="pt-1" style={{ borderTop: '1px solid #c5cff7' }}>
                            <div className="mb-1" style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#364fc7' }}>Laskutuserät</div>
                            <div style={{ overflowX: 'auto' }}>
                              <Table size="sm" className="align-middle mb-0" style={{ fontSize: '0.75rem' }}>
                                <thead className="table-light">
                                  <tr>
                                    <th>Erä</th>
                                    <th>Päivämäärä</th>
                                    <th className="text-end">Summa (€)</th>
                                    <th>Tila</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {thisTerms.map((t, i) => (
                                    <tr key={t.id ?? i}>
                                      <td>{t.name ?? `Erä ${i + 1}`}</td>
                                      <td className="text-muted">{t.date ?? '—'}</td>
                                      <td className="text-end">{t.net_amount != null ? moneyFmt(t.net_amount) : '—'}</td>
                                      <td>{t.invoiced ? <Badge bg="success" style={{ fontSize: '0.63rem' }}>Laskutettu</Badge> : <Badge bg="secondary" style={{ fontSize: '0.63rem' }}>Odottaa</Badge>}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {childProjects.length > 0 && (
                          <div className="ms-2">
                            {childProjects.map(renderProject)}
                          </div>
                        )}
                      </div>
                    );
                  };

                  const isAccepted = o => o.outcome === 'accepted' || o.outcome === 'won';
                  const isOpen = o => !o.outcome;

                  const acceptedOffers = offers.filter(isAccepted);
                  const openOffers = offers.filter(isOpen);
                  const otherOffers = offers.filter(o => !isAccepted(o) && !isOpen(o));

                  const filteredOffers = offerFilter === 'hyvaksytyt' ? acceptedOffers : openOffers;

                  const sortedOffers = [...filteredOffers].sort((a, b) =>
                    (b.originated_at ?? '') > (a.originated_at ?? '') ? 1 : -1
                  );

                  const offersWithChildren = sortedOffers.map(o => ({
                    offer: o,
                    children: contracts.filter(c => c.offer_id === o.id),
                  }));

                  const orphanContracts = contracts.filter(c => !c.offer_id || !offers.find(o => o.id === c.offer_id));
                  const orphanProjects = projects.filter(p => !p.contract_id || !contracts.find(c => c.id === p.contract_id));

                  return (
                    <>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        {[
                          { key: 'hyvaksytyt', label: 'Hyväksytyt', count: acceptedOffers.length, icon: 'bi-check-circle' },
                          { key: 'avoimet',    label: 'Avoimet',    count: openOffers.length,    icon: 'bi-clock'        },
                        ].map(f => (
                          <button
                            key={f.key}
                            onClick={() => setOfferFilter(f.key)}
                            className="btn btn-sm"
                            style={{
                              borderRadius: 20,
                              fontSize: '0.82rem',
                              padding: '2px 12px',
                              background: offerFilter === f.key ? '#04aa00' : 'transparent',
                              color: offerFilter === f.key ? '#fff' : '#6c757d',
                              border: offerFilter === f.key ? '1px solid #04aa00' : '1px solid #dee2e6',
                              fontWeight: offerFilter === f.key ? 600 : 400,
                            }}
                          >
                            <i className={`bi ${f.icon} me-1`} />{f.label}
                            {f.count > 0 && (
                              <span className="ms-1" style={{ opacity: 0.8 }}>({f.count})</span>
                            )}
                          </button>
                        ))}
                        {otherOffers.length > 0 && (
                          <span className="text-muted ms-1" style={{ fontSize: '0.78rem' }}>
                            + {otherOffers.length} muuta (hävitty/peruutettu)
                          </span>
                        )}
                      </div>

                    <div className="d-flex flex-column gap-3">
                      {sortedOffers.length === 0 && (
                        <p className="text-muted small">Ei {offerFilter === 'hyvaksytyt' ? 'hyväksyttyjä' : 'avoimia'} tarjouksia.</p>
                      )}
                      {offersWithChildren.map(({ offer: o, children }) => (
                        <div key={o.id} className="rounded p-3"
                          style={{ background: '#fff', border: '1px solid #dee2e6', borderLeft: '4px solid #04aa00' }}>
                          {/* Tarjous header */}
                          <div className="d-flex align-items-center gap-1 mb-2" style={{ color: '#04aa00', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            <i className="bi bi-file-earmark-check" />Tarjous
                          </div>
                          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                            <div className="fw-bold" style={{ fontSize: '1rem' }}>
                              {o.name || `#${o.id}`}
                              {o.is_lead && <Badge bg="info" className="ms-2 fw-normal">Liidi</Badge>}
                            </div>
                            {offerOutcomeBadge(o.outcome)}
                            {o.one_time_net_total != null && moneyFmt(o.one_time_net_total)}
                            {o.monthly_net_total != null && o.monthly_net_total !== 0 && moneyFmt(o.monthly_net_total, ' €/kk')}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.86rem' }}>
                            <div>
                              <div className="mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#04aa00' }}>Tuotteet</div>
                              {o.products?.length > 0 && <>
                                <div className="d-flex flex-column gap-1">
                                  {o.products.map((prod, i) => (
                                    <div key={prod.id ?? i} className="border rounded px-2 py-1 bg-light" style={{ fontSize: '0.8rem' }}>
                                      <div className="d-flex justify-content-between align-items-baseline gap-2">
                                        <span className="fw-semibold">{prod.name || '—'}</span>
                                        {prod.price != null && <span className="text-nowrap">{moneyFmt(prod.price, prod.monthly ? ' €/kk' : ' €')}</span>}
                                      </div>
                                      {prod.description && <div className="text-muted">{prod.description}</div>}
                                      <div className="d-flex gap-3 text-muted" style={{ fontSize: '0.75rem' }}>
                                        {prod.count != null && <span>{prod.count} kpl</span>}
                                        {prod.vat != null && <span>alv {prod.vat}%</span>}
                                        {prod.discount_percentage != null && <span>alennus {prod.discount_percentage}%</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>}
                            </div>
                            <div>
                              <div className="mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#04aa00' }}>Perustiedot</div>
                              <table className="table table-sm table-borderless mb-0" style={{ width: 'auto' }}>
                                <tbody>
                                  {o.probability != null && <tr><td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Todennäköisyys</td><td className="py-0">{o.probability} %</td></tr>}
                                  {o.originated_at && <tr><td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Luotu</td><td className="py-0">{o.originated_at.slice(0, 10)}</td></tr>}
                                  {o.valid_to && <tr><td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Voimassa</td><td className="py-0">{o.valid_to}</td></tr>}
                                  {o.resolved_at && <tr><td className="text-muted pe-3 py-0" style={{ fontSize: '0.786rem', whiteSpace: 'nowrap' }}>Ratkaistu</td><td className="py-0">{o.resolved_at.slice(0, 10)}</td></tr>}
                                </tbody>
                              </table>
                            </div>
                            <div>
                              <div className="mb-1" style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#04aa00' }}>Kuvaus</div>
                              {o.description && (
                                <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0.5rem 0.75rem', background: '#fff', minHeight: 60, color: '#1a1a1a', lineHeight: 1.6, fontSize: '0.9rem', fontWeight: 500 }}>{renderWithLinks(o.description)}</div>
                              )}
                            </div>
                          </div>
                          {/* Nested contracts */}
                          {children.length > 0 && (
                            <div className="ms-2">
                              {children.map(renderContract)}
                            </div>
                          )}

                          {/* Invoice footer — all invoices for this offer's contracts + projects */}
                          {(() => {
                            const contractIds = new Set(children.map(c => c.id));
                            const projectIds = new Set(
                              projects.filter(p => contractIds.has(p.contract_id)).map(p => p.id)
                            );
                            const offerInvoices = invoices.filter(inv =>
                              (inv.contract_id && contractIds.has(inv.contract_id)) ||
                              (inv.project_id && projectIds.has(inv.project_id))
                            );
                            if (!offerInvoices.length) return null;
                            return (
                              <div style={{ margin: '12px -12px -12px', borderTop: '2px solid #027a00', background: '#014d00', borderRadius: '0 0 6px 6px', padding: '10px 12px 10px' }}>
                                <div className="mb-1 d-flex align-items-center gap-1" style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#a3d9a3' }}>
                                  <i className="bi bi-receipt" />Laskut ({offerInvoices.length})
                                </div>
                                <div style={{ overflowX: 'auto' }}>
                                  <Table size="sm" className="align-middle mb-0" style={{ fontSize: '0.86rem', color: '#e0f2e0' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '1px solid #027a00' }}>
                                        <th className="fw-normal" style={{ fontSize: '0.78rem', color: '#a3d9a3', background: 'transparent' }}>Nimi</th>
                                        <th className="fw-normal" style={{ fontSize: '0.78rem', color: '#a3d9a3', background: 'transparent' }}>Laskupäivä</th>
                                        <th className="fw-normal" style={{ fontSize: '0.78rem', color: '#a3d9a3', background: 'transparent' }}>Eräpäivä</th>
                                        <th className="fw-normal text-end" style={{ fontSize: '0.78rem', color: '#a3d9a3', background: 'transparent' }}>Netto (€)</th>
                                        <th className="fw-normal" style={{ fontSize: '0.78rem', color: '#a3d9a3', background: 'transparent' }}>Tila</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {offerInvoices.map(inv => (
                                        <tr key={inv.id} style={{ borderBottom: '1px solid #025900' }}>
                                          <td style={{ background: 'transparent', color: '#e0f2e0' }}>{inv.name ?? `Lasku #${inv.id}`}</td>
                                          <td style={{ background: 'transparent', color: '#a3d9a3' }}>{inv.billing_date ?? '—'}</td>
                                          <td style={{ background: 'transparent', color: '#a3d9a3' }}>{inv.due_date ?? '—'}</td>
                                          <td className="text-end" style={{ background: 'transparent', color: '#e0f2e0' }}>{inv.net_amount != null ? inv.net_amount.toLocaleString('fi-FI') : '—'}</td>
                                          <td style={{ background: 'transparent' }}>
                                            {inv.paid
                                              ? <Badge bg="success" style={{ fontSize: '0.63rem' }}>Maksettu</Badge>
                                              : inv.sent
                                                ? <Badge bg="warning" text="dark" style={{ fontSize: '0.63rem' }}>Lähetetty</Badge>
                                                : <Badge bg="secondary" style={{ fontSize: '0.63rem' }}>Luonnos</Badge>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ))}

                      {/* Orphan contracts not linked to any offer */}
                      {orphanContracts.map(renderContract)}

                      {/* Orphan projects not linked to any contract */}
                      {orphanProjects.map(renderProject)}

                      {/* Orphan invoices — not linked to any known contract or project */}
                      {(() => {
                        const knownContractIds = new Set(contracts.map(c => c.id));
                        const knownProjectIds = new Set(projects.map(p => p.id));
                        const orphanInvoices = invoices.filter(inv =>
                          (!inv.contract_id || !knownContractIds.has(inv.contract_id)) &&
                          (!inv.project_id || !knownProjectIds.has(inv.project_id))
                        );
                        if (!orphanInvoices.length) return null;
                        return renderInvoiceFooter(orphanInvoices);
                      })()}
                    </div>
                    </>
                  );
                })()}
              </div>
            </>
          ))}
        </>
      )}
    </div>
  );
}
